import { useMemo } from "react";
import { Btn } from "@/components/Btn";
import { MetricPicker } from "@/components/MetricPicker";
import { fmtInt, fmtMetricValue, fmtMoney, fmtX, getMetric } from "@/lib/metrics";
import type { App, CampaignStatsRow } from "@/lib/types";

export type TopSlot = {
  id: string;
  name: string;
  metric: string;
};

function parseNum(v: string | null | undefined): number {
  if (v == null) return 0;
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

// The three always-on columns in the Top Campaigns table. We hide the
// slot-metric column when it duplicates one of these.
const FIXED_COLUMN_METRICS = new Set(["spend", "installs", "roas_d4"]);
function isFixedColumn(metricId: string): boolean {
  return FIXED_COLUMN_METRICS.has(metricId);
}

export function TopCampaignsCard({
  rows,
  apps,
  slots,
  onSlotsChange,
  activeSlot,
  onActiveSlot,
  defaultSlot,
  onDefaultSlot,
  loading,
}: {
  rows: CampaignStatsRow[];
  apps: App[];
  slots: TopSlot[];
  onSlotsChange: (next: TopSlot[]) => void;
  activeSlot: string;
  onActiveSlot: (id: string) => void;
  defaultSlot: string;
  onDefaultSlot: (id: string) => void;
  loading?: boolean;
}) {
  const cur = slots.find((s) => s.id === activeSlot) || slots[0];
  const m = getMetric(cur.metric);
  const appMap = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);

  const sorted = useMemo(() => {
    // "Top campaigns By X" always means highest-X first, regardless of
    // whether higher-X is "good" (ROAS) or "bad" (CPA). This is a
    // different sort intent from the leaderboard cards which want to
    // surface best/worst performers.
    return [...rows]
      .map((r) => ({ row: r, metric: m.fromRow(r) ?? 0 }))
      .filter((x) => x.metric > 0)
      .sort((a, b) => b.metric - a.metric)
      .slice(0, 6);
  }, [rows, m]);

  const maxVal = Math.abs(sorted[0]?.metric || 1);

  const updateSlotMetric = (slotId: string, metricId: string) => {
    onSlotsChange(
      slots.map((s) =>
        s.id === slotId
          ? { ...s, metric: metricId, name: "By " + getMetric(metricId).short }
          : s
      )
    );
  };

  return (
    <div className="card">
      <div className="card-head">
        <h3>Top campaigns</h3>
        <div className="row gap-8">
          <div className="chip-group">
            {slots.map((s) => (
              <button
                key={s.id}
                className={s.id === activeSlot ? "active" : ""}
                onClick={() => onActiveSlot(s.id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                {defaultSlot === s.id && (
                  <span style={{ color: "#eab308", fontSize: 10 }}>★</span>
                )}
                {s.name}
              </button>
            ))}
          </div>
          <MetricPicker
            value={cur.metric}
            onChange={(mid) => updateSlotMetric(cur.id, mid)}
            trigger={({ toggle }) => (
              <Btn size="sm" onClick={toggle} title={`Change metric for "${cur.name}"`}>
                Edit slot
              </Btn>
            )}
          />
          {defaultSlot !== activeSlot && (
            <Btn
              size="sm"
              onClick={() => onDefaultSlot(activeSlot)}
              title="Make this the default view"
            >
              ★ Default
            </Btn>
          )}
        </div>
      </div>
      <div className="table-scroll">
        <table className="dt" style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>App</th>
              <th className="num">Spend</th>
              <th className="num">Installs</th>
              <th className="num">ROAS D4</th>
              {/* Show the slot metric only when it isn't already a fixed column. */}
              {!isFixedColumn(cur.metric) && (
                <th className="num">{m.short}</th>
              )}
              <th className="num" style={{ width: 220 }}>
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: "center", padding: 14 }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: "center", padding: 14 }}>
                  No campaigns with {m.short.toLowerCase()} in this period.
                </td>
              </tr>
            )}
            {sorted.map(({ row, metric }, i) => {
              const pct = Math.abs(metric) / (maxVal || 1);
              const appName =
                row.app_id != null ? appMap.get(row.app_id)?.name : null;
              const spend = parseNum(row.spend_usd);
              const roasD4 = parseNum(row.roas_d4);
              const roasCls =
                roasD4 >= 1.5 ? "val-pos" : roasD4 > 0 && roasD4 < 0.8 ? "val-neg" : "";
              return (
                <tr key={row.campaign_id}>
                  <td>
                    <div className="row gap-8">
                      <span className="row-ico gray">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {row.campaign_name ?? `Campaign ${row.campaign_id}`}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: "var(--c-text-3)" }}>
                    {appName ?? "—"}
                  </td>
                  <td className="num">${fmtMoney(spend)}</td>
                  <td className="num">{fmtInt(row.installs ?? 0)}</td>
                  <td className={"num " + roasCls}>
                    {roasD4 > 0 ? `${fmtX(roasD4)}×` : "—"}
                  </td>
                  {!isFixedColumn(cur.metric) && (
                    <td className="num">
                      <b>{fmtMetricValue(cur.metric, metric)}</b>
                    </td>
                  )}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: "var(--c-surface-sunk)",
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${pct * 100}%`,
                            height: "100%",
                            background:
                              "linear-gradient(90deg, var(--c-accent-400), var(--c-accent-600))",
                          }}
                        />
                      </div>
                      <div
                        className="mono"
                        style={{
                          fontSize: 11.5,
                          color: "var(--c-text-3)",
                          width: 44,
                          textAlign: "right",
                        }}
                      >
                        {Math.round(pct * 100)}%
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
